/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/**
 * @override
 * - Re-export base LocaleText component
 * - Add LANGUAGE_NAMES mapping for short language display format
 * - Add getLocaleDisplayName helper for "Language (CODE)" format
 */

// Re-export the base LocaleText component
export {default} from '@salesforce/retail-react-app/app/components/locale-text';

/**
 * Language names mapping for display format: "Language (CODE)"
 * Used by LocaleSelector for compact locale display
 */
export const LANGUAGE_NAMES = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
  de: 'Deutsch',
  it: 'Italiano',
  pt: 'Português',
  nl: 'Nederlands',
  zh: '简体中文',
  ja: '日本語',
  ko: '한국어',
  ar: 'العربية',
  ru: 'Русский',
  pl: 'Polski',
  sv: 'Svenska',
  da: 'Dansk',
  fi: 'Suomi',
  no: 'Norsk',
  tr: 'Türkçe',
  th: 'ไทย',
  vi: 'Tiếng Việt',
  id: 'Bahasa Indonesia',
  ms: 'Bahasa Melayu',
  hi: 'हिन्दी',
  bn: 'বাংলা',
  ta: 'தமிழ்',
  he: 'עברית',
  el: 'Ελληνικά',
  cs: 'Čeština',
  hu: 'Magyar',
  ro: 'Română',
  sk: 'Slovenčina',
};

/**
 * Get short language code from locale ID
 * @param {string} localeId - Locale ID (e.g., 'en-AU')
 * @returns {string} Uppercase language code (e.g., 'EN')
 */
export const getLanguageCode = (localeId) => localeId?.split('-')[0]?.toUpperCase() || '';

/**
 * Get display name in format "Language (CODE)"
 * @param {string} localeId - Locale ID (e.g., 'en-AU')
 * @returns {string} Display name (e.g., 'English (EN)')
 */
export const getLocaleDisplayName = (localeId) => {
  const code = getLanguageCode(localeId);
  const langCode = localeId?.split('-')[0]?.toLowerCase();
  const langName = LANGUAGE_NAMES[langCode] || code;

  return `${langName} (${code})`;
};
